import { createSelector } from 'reselect';
import { combineReducers, AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import megatron from '@stencil/megatron';
import getCookie from '@stencil/flights-utils/src/get-cookie';

export interface IUserStoreState {
  userData: string;
  userInfo: IUserInfo;
  gocashInfo: IGocashInfo;
  isLoading: boolean;
  showLoginPopup: boolean;
  isLoggedIn: boolean;
  userDetailsFetched: boolean;
}

export interface IUserInfo {
  first_name?: string;
  last_name?: string;
  email?: string;
  userid: number;
  profile_type?: string;
  image_url?: string;
  hashedemail?: string;
}

export interface IGocashInfo {
  success: boolean;
  data: {
    credits_id?: string;
    np_amt?: number;
    p_amt?: number;
    is_locked?: number;
    b_amt?: number;
    t_amt?: number;
  };
}

export const UPDATE_USER_DETAILS = 'UPDATE_USER_DETAILS';
export const TOGGLE_LOADING_USER_DETAILS = 'TOGGLE_LOADING_USER_DETAILS';
export const TOGGLE_LOGIN_POPUP = 'TOGGLE_LOGIN_POPUP';
export const UPDATE_USER_DETAILS_FETCHED = 'UPDATE_USER_DETAILS_FETCHED';

export interface IUpdateUserDetails {
  type: typeof UPDATE_USER_DETAILS;
  actionData: {
    userInfo: IUserStoreState['userInfo'];
    gocashInfo: IUserStoreState['gocashInfo'];
    isLoggedIn: IUserStoreState['isLoggedIn'];
  };
}

export interface IToggleLoadingUserDetails {
  type: typeof TOGGLE_LOADING_USER_DETAILS;
  actionData: IUserStoreState['isLoading'];
}

export interface IUpdateUserDetailsFetched {
  type: typeof UPDATE_USER_DETAILS_FETCHED;
  actionData: IUserStoreState['userDetailsFetched'];
}
export interface IToggleLoginPopup {
  type: typeof TOGGLE_LOGIN_POPUP;
  actionData: IUserStoreState['showLoginPopup'];
}

export type IUserActionTypes =
  | IUpdateUserDetails
  | IToggleLoadingUserDetails
  | IToggleLoginPopup
  | IUpdateUserDetailsFetched;

export function updateUserDetailsAction(
  actionData: IUpdateUserDetails['actionData']
): IUpdateUserDetails {
  return {
    type: UPDATE_USER_DETAILS,
    actionData
  };
}

export function toggleLoadingUserDetailsAction(
  actionData: IToggleLoadingUserDetails['actionData']
): IToggleLoadingUserDetails {
  return {
    type: TOGGLE_LOADING_USER_DETAILS,
    actionData
  };
}

export function updateUserDetailsFetched(
  actionData: IUpdateUserDetailsFetched['actionData']
): IUpdateUserDetailsFetched {
  return {
    type: UPDATE_USER_DETAILS_FETCHED,
    actionData
  };
}

export function toggleLoginPopup(actionData: IToggleLoginPopup['actionData']): IToggleLoginPopup {
  return {
    type: TOGGLE_LOGIN_POPUP,
    actionData
  };
}

export function getUserServiceURL() {
  return `https://userservice.goibibo.com/ext/web/desktop/user/details`;
}

const getUserServiceRequestData = () => {
  return {
    query: [
      [
        {
          name: 'extendedUser',
          keys: [
            'profileId',
            'uuid',
            'primaryEmailId',
            'userType',
            'profileType',
            'loginInfoList',
            'personalDetails'
          ]
        }
      ],
      [
        {
          name: 'personalDetails',
          keys: ['gender', 'dateOfBirth']
        }
      ]
    ]
  };
};

export const fetchUserDetailsThunk = (): ThunkAction<void, UserAppState, {}, AnyAction> => {
  return (dispatch: ThunkDispatch<UserAppState, {}, AnyAction>, getState: () => UserAppState) => {
    const url = getUserServiceURL();

    const oauth = getCookie('OAUTH-GOIBIBO') || '';

    if (oauth) {
      dispatch(toggleLoadingUserDetailsAction(true));
      var headers = new Headers();
      headers.append('content-type', 'application/json');
      headers.append('authorization', 'jkO7Ivpwsh4KaQ9');
      headers.append('currency', 'inr');
      headers.append('language', 'eng');
      headers.append('region', 'in');
      headers.append('x-request-tracker', JSON.stringify(new Date().getTime()));
      headers.append(
        'user-identifier',
        JSON.stringify({
          type: 'auth',
          deviceId: 'deviceId',
          os: 'os',
          osVersion: 'osVersion',
          appVersion: 'appVersion',
          imie: 'imie',
          ipAddress: 'ipAddress',
          timeZone: 'timeZone',
          value: oauth || ''
        })
      );

      megatron({
        credentials: 'omit',
        name: 'userservice',
        type: 'POST',
        url,
        headers,
        requestData: JSON.stringify(getUserServiceRequestData())
      })
        .then(response => {
          if (response?.result?.extendedUser) {
            const userData = response?.result?.extendedUser || {};

            const profileId = userData?.profileId.slice(2);

            const userInfo = {
              auth_token: oauth,
              first_name: userData?.personalDetails?.name?.firstName || '',
              last_name: userData?.personalDetails?.name?.lastName || '',
              gender: userData?.personalDetails?.gender || '',
              // email: userData?.primaryEmailId || '',
              email: '',
              profile_type: userData?.profileType || 'personal',
              userid: parseInt(profileId, 10) || 0,
              profileId: userData?.profileId || '',
              username: userData?.uuid || '',
              uuid: userData?.uuid || ''
            };
            window.userInfo = userInfo;

            dispatch(
              updateUserDetailsAction({
                userInfo,
                isLoggedIn: userData && userData.profileId ? true : false,
                userDetailsFetched: true,
                isLoading: false
              })
            );
          } else {
            dispatch(
              updateUserDetailsAction({
                userData: '',
                gocashInfo: { data: null },
                userInfo: {},
                isLoggedIn: false,
                userDetailsFetched: true,
                isLoading: false
              })
            );
          }
        })
        .catch(err => {
          dispatch(toggleLoadingUserDetailsAction(false));
          dispatch(updateUserDetailsFetched(true));
        });
    } else {
      dispatch(updateUserDetailsFetched(true));
    }
  };
};

const initialState: IUserStoreState = {
  isLoading: false,
  userData: '',
  userInfo: { profile_type: 'personal', userid: null },
  gocashInfo: null,
  isLoggedIn: false,
  showLoginPopup: false,
  userDetailsFetched: false
};

export function userDetailsReducer(state = initialState, action: IUserActionTypes) {
  switch (action.type) {
    case UPDATE_USER_DETAILS:
      return {
        ...state,
        ...action.actionData
      };

    case TOGGLE_LOADING_USER_DETAILS:
      return {
        ...state,
        isLoading: action.actionData
      };

    case UPDATE_USER_DETAILS_FETCHED:
      return {
        ...state,
        userDetailsFetched: action.actionData
      };

    case TOGGLE_LOGIN_POPUP:
      return {
        ...state,
        showLoginPopup: action.actionData
      };

    default:
      return state;
  }
}

export const userReducer = {
  user: userDetailsReducer
};

export const userSelector = createSelector(
  (state: UserAppState) => state.user,
  item => item
);

const rootReducer = combineReducers(userReducer);

export type UserAppState = ReturnType<typeof rootReducer>;
